/// <amd-module name="Analytics/Boards/BurndownQueries/EffortSnapshotsQuery" />
import { FieldFilter, WorkItemFieldDescriptor } from "Analytics/Boards/BoardsQueries/DataContracts";
import { AggregationMode, ModefulValueSetting, TeamScope } from "Analytics/Boards/BoardsQueries/DataContracts";
import { WitFieldUtilities } from "Analytics/Boards/BoardsQueries/WitFieldUtilities";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { DateSKParser } from "Analytics/Common/Utilities/DateSKParser";
import * as PublicProjectsQueryHelper from "Analytics/Common/Utilities/PublicProjectsQueryHelper";
import { DateSamplingSeries, latestODataVersion, QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";
import { IVssPageContext } from "VSS/Platform/Context";
import { uniqueSort } from "VSS/Platform/Util/Array";
import { WorkItemEffort } from "./BurndownDataContract";
import { BurndownQueryBase } from "./BurndownQueryBase";

export interface ODataWorkItemSnapshotAggregation {
    WorkItemType: string;
    DateSK: number;
    StateCategory: string;
    AggregatedEffort: number;
    State: string;
}

/**
 * This query buckets workitems by date, state and work item type, aggregating by effort by these buckets.
 * This is the primary query of burndown and burnup widgets, used to draw the chart by date and effort
 */
export class EffortSnapshotsQuery extends BurndownQueryBase<WorkItemEffort[]> {
    private static generateQueryOptions(
        pageContext: IVssPageContext,
        dateSeries: DateSamplingSeries,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        workItemFieldDescriptors: WorkItemFieldDescriptor[],
        showResolvedItemsAsCompleted = false
    ): ODataQueryOptions {
        // $apply
        const teams = uniqueSort(teamScopes.map(scope => scope.teamId));
        const teamsClause = QueryUtilities.isInArray("t/TeamSK", teams, false /* useQuotes */);

        const projects = uniqueSort(teamScopes.map(scope => scope.projectId));
        const projectsClause = QueryUtilities.isInArray("ProjectSK", projects, false /* useQuotes */);
        const projectsTagClause = QueryUtilities.isInArray("t/ProjectSK", projects, false /* useQuotes */);

        const dateSKs = dateSeries.sampleDates.map(date => DateSKParser.dateStringToDateSK(date));
        const earliestDateSK = Math.min(...dateSKs);
        const datesClause = QueryUtilities.generateDatesClause("DateSK", dateSeries);

        const workItemTypesClause = QueryUtilities.isInArray("WorkItemType", workItemTypes, true /* useQuotes */);

        let $apply =
            `filter(` +
            `(${projectsClause})` +
            ` and Teams/any(t:${teamsClause})` +
            ` and (${workItemTypesClause})` +
            ` and StateCategory ne null` +
            ` and (RevisedDateSK eq null or RevisedDateSK gt ${earliestDateSK})` + // Added to improve query perf
            ` and (${datesClause})`;

        const filtersClause = WitFieldUtilities.getFiltersClause(filters, workItemFieldDescriptors);
        if (filtersClause) {
            if(filtersClause.includes('Tags/any(t:t/TagName'))
            {
                const removeLastChar = filtersClause.slice(0, -1);              
                $apply += ` and (${removeLastChar} and ${projectsTagClause}))`;                
            }
            else
            {
                $apply += ` and (${filtersClause})`;
            }        
        }

        $apply += ")";

        let aggregateFunction = "aggregate($count as AggregatedEffort)";
        if (aggregation.identifier === AggregationMode.Sum) {
            const analyticsEffortFieldName = WitFieldUtilities.getFieldODataPropertyName(aggregation.settings);
            aggregateFunction = `aggregate(${analyticsEffortFieldName} with sum as AggregatedEffort)`;
        }

        if(showResolvedItemsAsCompleted){
            $apply += `/groupby((DateSK,State,StateCategory,WorkItemType),${aggregateFunction})`;
        }
        else{
            $apply += `/groupby((DateSK,StateCategory,WorkItemType),${aggregateFunction})`;
        }
        
        return PublicProjectsQueryHelper.forceProjectScoping(pageContext, projects, {
            entityType: "WorkItemSnapshot",
            oDataVersion: latestODataVersion,
            $apply: $apply
        });
    }
    constructor(
        pageContext: IVssPageContext,
        dates: DateSamplingSeries,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        workItemFieldDescriptors: WorkItemFieldDescriptor[],
        showResolvedItemsAsCompleted = false,
        commandName?: string
    ) {
        super(
            EffortSnapshotsQuery.generateQueryOptions(pageContext, dates, teamScopes, filters, aggregation, workItemTypes, workItemFieldDescriptors, showResolvedItemsAsCompleted),
            commandName
        );
    }

    public getQueryName(): string {
        return "EffortSnapshotsQuery";
    }

    protected interpretQueryResults(data: { value: ODataWorkItemSnapshotAggregation[] }): WorkItemEffort[] {
        // This null filter is only needed because we don't include a 'ne null' filter for effort in the query.
        // As of this writing the reason we don't include it in the query is to get better SQL perf.
        // That may or may not change in the future.
        const filteredValues = data.value.filter(value => value.AggregatedEffort != null);
        return filteredValues.map(value => {
            return {
                WorkItemType: value.WorkItemType,
                Date: DateSKParser.parseDateSKAsDateString(value.DateSK),
                StateCategory: value.StateCategory,
                AggregatedEffort: value.AggregatedEffort,
                State: value.State
            };
        });
    }
}
